import { paymentMethodTypes } from 'constants/companyConstants';

/**
 * Parse Payment Methods from GraphQL to frontend format
 * This parser handles the simple payment method list (without campaign context)
 * 
 * To determine which payment method is selected for a campaign:
 * 1. Query the campaign and include campaignPaymentMethods
 * 2. Extract the customerPaymentMethodId from campaignPaymentMethods
 * 3. Match it against the payment methods list on the frontend
 */
export const parsePaymentMethodsFromGraphQL = (paymentMethodsData) => {
  if (!paymentMethodsData || !Array.isArray(paymentMethodsData)) return [];

  return paymentMethodsData.map((pm) => {
    if (!pm) return null;

    // Construct fullAddress from address components
    const fullAddressArray = [pm.address, pm.city, pm.state].filter((el) => el);

    // Map billing type from GraphQL to frontend constants
    const billingTypeName = pm.billingType?.typeName?.toLowerCase();
    let billingType = billingTypeName // Default to card
    
    if (billingTypeName === 'invoice') {
      billingType = paymentMethodTypes.INVOICE;
    } else if (billingTypeName === 'credit card') {
      billingType = paymentMethodTypes.CARD;
    } else if (billingTypeName === 'ach') {
      billingType = paymentMethodTypes.ACH;
    }

    return {
      id: pm.id,
      lastFour: pm.lastFour,
      expMonth: pm.expMonth,
      expYear: pm.expYear,
      email: pm.email || null,
      billingType,
      billingTypeName,
      processor: pm.processor?.typeName || null,
      fullName: pm.fullName,
      address: pm.address,
      city: pm.city,
      state: pm.state,
      zip: pm.zip,
      fullAddress: fullAddressArray.join(', '),
      isDefault: pm.defaultPaymentMethod || false
    };
  }).filter(Boolean); // Remove any null entries
};

/**
 * Parse Payment Method from GraphQL mutation response
 */
export const parseAddPaymentMethodResponse = (paymentMethod) => {
  if (!paymentMethod) return null;

  const fullAddressArray = [paymentMethod.address, paymentMethod.city, paymentMethod.state].filter((el) => el);

  const billingTypeName = paymentMethod.billingType?.typeName?.toLowerCase();
  let billingType = paymentMethodTypes.CARD;
  
  if (billingTypeName === 'invoice') {
    billingType = paymentMethodTypes.INVOICE;
  } else if (billingTypeName === 'credit card' || billingTypeName === 'ach') {
    billingType = paymentMethodTypes.CARD;
  }

  return {
    id: paymentMethod.id,
    lastFour: paymentMethod.lastFour,
    expMonth: paymentMethod.expMonth,
    expYear: paymentMethod.expYear,
    email: paymentMethod.email || null,
    billingType,
    billingTypeName,
    processor: paymentMethod.processor?.typeName || null,
    fullName: paymentMethod.fullName,
    address: paymentMethod.address,
    city: paymentMethod.city,
    state: paymentMethod.state,
    zip: paymentMethod.zip,
    fullAddress: fullAddressArray.join(', '),
    isDefault: paymentMethod.defaultPaymentMethod || false
  };
};

/**
 * Transform frontend payment data to GraphQL mutation input
 */
export const transformToAddPaymentMethodInput = (data, customerId, stripeCustomerId, billingTypeId, processorId) => {
  const input = {
    customerId,
    billingTypeId,
    cardholderName: data.fullName,
    billingAddress: {
      street: data.address,
      city: data.city,
      state: data.state,
      zip: data.zip,
      address2: data.address2 || ''
    },
    lastFour: data.cc?.slice(-4) || '0000',
    cvv: data.cvv,
    cardNumber: data.cc,
    expMonth: parseInt(data.expMonth, 10),
    expYear: parseInt(data.expYear, 10),
    isDefault: true,
    processorCustomerId: stripeCustomerId,
    processorId
  };

  return input;
};

